import Button from "@shared/components/ui/Button";
import theme from "@shared/utils/themeColors";
import React, { JSX } from "react";
import { CalendarEvent, EventCategory } from "../types/calendar.types";
import { getColorClasses } from "../utils/colorUtils";

interface EventDetailsProps {
  event: CalendarEvent;
  category?: EventCategory;
  onEdit?: () => void;
  onClose: () => void;
  isAdmin: boolean;
}

const EventDetails: React.FC<EventDetailsProps> = ({
  event,
  category,
  onEdit,
  onClose,
  isAdmin,
}) => {
  const formatDate = (timestamp: any): string => {
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  };

  const formatDescription = (description: string): JSX.Element => {
    // Simple URL detection regex
    const urlRegex = /(https?:\/\/[^\s]+)/g;

    // Split by URLs and map each part
    const parts = description.split(urlRegex);
    const matches = description.match(urlRegex) || [];

    return (
      <>
        {parts.map((part, i) => {
          // If this part is followed by a URL, add the URL as a link
          const url = i < matches.length ? matches[i] : null;
          return (
            <React.Fragment key={i}>
              {part}
              {url && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:underline"
                >
                  {url}
                </a>
              )}
            </React.Fragment>
          );
        })}
      </>
    );
  };

  return (
    <div className="space-y-4">
      <div
        className={`p-3 rounded-lg ${getColorClasses(category?.color || "gray").bg} ${getColorClasses(category?.color || "gray").bgDark}`}
      >
        <h2
          className={`text-xl font-semibold ${getColorClasses(category?.color || "gray").text} ${getColorClasses(category?.color || "gray").textDark}`}
        >
          {event.title}
        </h2>
        <p
          className={`text-sm ${getColorClasses(category?.color || "gray").textLight} ${getColorClasses(category?.color || "gray").textLightDark}`}
        >
          {category?.name || "Uncategorized"}
        </p>
      </div>

      <div>
        <h3 className={`text-sm font-medium ${theme.text.secondary}`}>
          Date & Time
        </h3>
        <p className={`${theme.text.primary}`}>
          {formatDate(event.startDate)}
          {event.endDate && (
            <>
              <br />
              <span className="text-sm">to</span>
              <br />
              {formatDate(event.endDate)}
            </>
          )}
        </p>
      </div>

      {event.description && (
        <div>
          <h3 className={`text-sm font-medium ${theme.text.secondary}`}>
            Description
          </h3>
          <p className={`${theme.text.primary} whitespace-pre-wrap`}>
            {formatDescription(event.description)}
          </p>
        </div>
      )}

      {event.url && (
        <div>
          <h3 className={`text-sm font-medium ${theme.text.secondary}`}>URL</h3>
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:underline"
          >
            {event.url}
          </a>
        </div>
      )}

      <div className="flex justify-end space-x-2 pt-4">
        {isAdmin && onEdit && (
          <Button
            type="button"
            onClick={onEdit}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
          >
            Edit
          </Button>
        )}
        <Button type="button" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
};

export default EventDetails;
